import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FilePlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import type { WorkspaceSection, DocumentPriority } from '../lib/types';

// Mirrors the backend's get_section_display_paths(), so subsection labels are unambiguous.
function flattenSections(sections: WorkspaceSection[]): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = [];
  for (const root of sections) {
    out.push({ id: root.id, label: root.name });
    for (const sub of root.subsections ?? []) {
      out.push({ id: sub.id, label: `${root.name} > ${sub.name}` });
    }
  }
  return out;
}

const PRIORITIES: { value: DocumentPriority; label: string }[] = [
  { value: 'required', label: 'Required' },
  { value: 'recommended', label: 'Recommended' },
  { value: 'optional', label: 'Optional' },
];

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand';

export default function AddDocumentTypePage() {
  const { authFetch } = useAuth();
  const { sections, refresh } = useWorkspace();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const sectionOptions = useMemo(() => flattenSections(sections), [sections]);

  const [name, setName] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<DocumentPriority>('optional');
  const [help, setHelp] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim() !== '' && sectionId !== '' && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await authFetch('/workspace/types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          section_id: sectionId,
          description: description.trim() || null,
          priority,
          help: help.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        showToast(err?.detail || 'Failed to add document type', 'error');
        return;
      }
      const doc = await res.json();
      await refresh();
      showToast('Document type added', 'success');
      navigate(`/types/${doc.id}`);
    } catch {
      showToast('Failed to add document type', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="pb-6 mb-6 border-b border-softgrey">
        <h3 className="text-lg font-semibold text-graphite">Add document type</h3>
        <p className="text-sm text-coolslate mt-1">
          Create a new document type and place it in a section. A document type belongs to exactly one section.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <FormField label="Name" required>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Board Meeting Minutes"
            className={inputCls}
            autoFocus
          />
        </FormField>

        <FormField label="Section" required>
          <select
            value={sectionId}
            onChange={e => setSectionId(e.target.value)}
            className={`${inputCls} cursor-pointer appearance-none`}
          >
            <option value="" disabled>Select a section…</option>
            {sectionOptions.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Priority">
          <select
            value={priority}
            onChange={e => setPriority(e.target.value as DocumentPriority)}
            className={`${inputCls} cursor-pointer appearance-none`}
          >
            {PRIORITIES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Description">
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What this document is and why it's needed (optional)."
            rows={3}
            className={`${inputCls} resize-y`}
          />
        </FormField>

        <FormField label="Help">
          <textarea
            value={help}
            onChange={e => setHelp(e.target.value)}
            placeholder="Longer guidance shown in the help panel. Markdown supported (optional)."
            rows={4}
            className={`${inputCls} resize-y`}
          />
        </FormField>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FilePlus className="w-4 h-4" />
            {submitting ? 'Adding…' : 'Add document type'}
          </button>
          <Link
            to="/library"
            className="px-4 py-2.5 text-sm font-medium text-coolslate hover:text-graphite transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-graphite">
        {label}
        {required && <span className="text-state-error ml-0.5">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
